import React, { useEffect, useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from "../components/ui/Input";
import { Agent } from '../types';
import { useAgentsApi } from '../apis/agents-apis';

interface AgentSelectProps {
  selectedAgents: Agent[];
  onChange: (agents: Agent[]) => void;
  readOnly?: boolean;
}

export const AgentSelect: React.FC<AgentSelectProps> = ({
  selectedAgents,
  onChange,
  readOnly = false
}) => {
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const agentsApi = useAgentsApi();

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const getAllAgents = false;
        const agents = await agentsApi.getAgents(getAllAgents);
        setAllAgents(agents);
        setError(null);
      } catch (err) {
        setError('Failed to load agents');
        console.error('Error fetching agents:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgents();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredAgents = searchQuery.length >= 1 ? allAgents.filter(agent => {
    const searchLower = searchQuery.toLowerCase();
    return (
      agent.agentName.toLowerCase().includes(searchLower) ||
      agent.agentDescription.toLowerCase().includes(searchLower) ||
      agent.agentTags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  }) : [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsDropdownOpen(value.length >= 1);
  };

  const handleAgentToggle = (agent: Agent) => {
    if (readOnly) return;
    
    const newSelectedAgents = isAgentSelected(agent)
      ? selectedAgents.filter(s => s.agentId !== agent.agentId)
      : [...selectedAgents, agent];
    
    onChange(newSelectedAgents);
  };

  const isAgentSelected = (agent: Agent) => 
    selectedAgents.some(selected => selected.agentId === agent.agentId);

  const removeAgent = (agentToRemove: Agent) => {
    if (readOnly) return;
    onChange(selectedAgents.filter(agent => agent.agentId !== agentToRemove.agentId));
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Selected Agents Display */}
      <div className="flex flex-wrap gap-2">
        {selectedAgents.map(agent => (
          <div
            key={agent.agentId}
            className="bg-blue-50 border border-blue-200 rounded-md px-3 py-1 flex items-center gap-2"
          >
            <span className="text-sm text-blue-700">{agent.agentName}</span>
            {!readOnly && (
              <button
                onClick={() => removeAgent(agent)}
                className="text-blue-400 hover:text-blue-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Search Input and Dropdown */}
      {!readOnly && (
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search agents..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10"
              disabled={isLoading}
            />
          </div>

          {/* Dropdown Results */}
          {isDropdownOpen && searchQuery.length >= 1 && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">Loading agents...</div>
              ) : filteredAgents.length > 0 ? (
                <div className="py-1">
                  {filteredAgents.map(agent => (
                    <div
                      key={agent.agentId}
                      onClick={() => {
                        handleAgentToggle(agent);
                        setSearchQuery('');
                        setIsDropdownOpen(false);
                        inputRef.current?.focus();
                      }}
                      className={`
                        px-4 py-2 cursor-pointer hover:bg-gray-50
                        ${isAgentSelected(agent) ? 'bg-blue-50' : ''}
                      `}
                    >
                      <div className="font-medium text-gray-900">{agent.agentName}</div>
                      <div className="text-sm text-gray-600">{agent.agentDescription}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No agents found matching your search
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentSelect;